"use client";

import { DataTable } from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber } from "@/components/tools/tool-ui";
import { percentText } from "@/lib/tool-kit/research-design/format";
import type { Rate, RateGroup } from "@/lib/tool-kit/research-ops/aapor";

export interface AaporResultLabels {
  groupNames: Record<RateGroup, string>;
  rateNames: Record<Rate["id"], string>;
  colRate: string;
  colFormula: string;
  colWorking: string;
  colValue: string;
  needsE: string;
  noCases: string;
}

const GROUPS: readonly RateGroup[] = [
  "response",
  "cooperation",
  "refusal",
  "contact",
];

/** One table per family of rates, each row showing the formula and the numbers in it. */
export function AaporResults({
  rates,
  labels,
}: {
  rates: readonly Rate[];
  labels: AaporResultLabels;
}) {
  const fmt = useToolNumber();
  const valueText = (rate: Rate) => {
    if (rate.value !== null) {
      return percentText(fmt, rate.value, 1);
    }
    return rate.usesE && Number.isNaN(rate.denominator)
      ? labels.needsE
      : labels.noCases;
  };
  const workingText = (rate: Rate) =>
    Number.isNaN(rate.denominator)
      ? "—"
      : `${fmt(rate.numerator)} / ${fmt(rate.denominator, { maximumFractionDigits: 2 })}`;
  return (
    <div className="space-y-6">
      {GROUPS.map((group) => (
        <div className="space-y-3" key={group}>
          <h3 className={WIDGET_H3}>{labels.groupNames[group]}</h3>
          <DataTable
            head={[
              labels.colRate,
              labels.colFormula,
              labels.colWorking,
              labels.colValue,
            ]}
            rows={rates
              .filter((rate) => rate.group === group)
              .map((rate) => [
                labels.rateNames[rate.id],
                rate.formula,
                workingText(rate),
                valueText(rate),
              ])}
          />
        </div>
      ))}
    </div>
  );
}
